import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import moment from 'moment';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { Store } from '@ngxs/store';
import { ConfigActions, ConfigState } from '@store/config.state';
import { Config, ReportInfo, ReportTypes } from '@other/interfaces';
import { csvToJson, csvToJsonNamed, deliveriesToJson, htmlToJson } from '@other/functions';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { ILanguage } from '@other/interfaces';
import { LogService } from "@services/log.service";


@Injectable()
export class ApiService {
  @SelectSnapshot(ConfigState.GetLanguage) language: ILanguage;
  private logger: LogService;

  constructor(
    private http: HttpClient,
    private _store: Store,
    private _logger: LogService
  ) {
    (window as any).api = this;
    this.logger = _logger;
  }

  /** Holder for report dates */
  reportDates = {
    orderIntake: '',
    orderBacklog: '',
    productionProgram: '',
    allocation: '',
    plantStock: '',
    orderIntakeHistory: '',
    deliveries: ''
  };

  // Local function for expected response content type validation
  // Returns empty object for params (Angular HttpClient params are for query parameters only)
  // Content type validation will be done manually in response handling
  expectResponse(mime: string): Record<string, never> {
    // Return empty object since params in Angular HttpClient is for query parameters,
    // not response validation. Content type validation will be handled in response pipeline.
    return {};
  }

  // Helper function to validate response content type
  // Can be used in RxJS operators like tap() or map() to validate responses
  validateContentType(expectedMime: string, actualContentType: string | null): boolean {
    if (!actualContentType) {
      this.logger.error("validateContentType","ApiService", "!!! No content type found !!!")
      return false;
    }
    
    // Extract base MIME type (remove charset, etc.)
    const baseMime = actualContentType.split(';')[0].trim();
    
    // Handle partial matches (e.g., '/html' should match 'text/html', '/csv' should match 'application/csv' or 'text/csv')
    if (expectedMime.startsWith('/')) {
      const suffix = expectedMime.substring(1); // Remove leading '/'
      const returnValue = baseMime.includes(suffix);
      this.logger.info("validateContentType","ApiService", `Expected: ${expectedMime}, Actual: ${baseMime}, Match: ${returnValue}`)
      return returnValue;
    }
    
    // For exact matches, also allow common variations (e.g., 'application/json' matches 'text/json')
    const returnValue = baseMime === expectedMime || 
                       (expectedMime === 'application/json' && (baseMime === 'text/json' || baseMime === 'application/json'));
    this.logger.info("validateContentType","ApiService", `Expected: ${expectedMime}, Actual: ${baseMime}, Match: ${returnValue}`)
    return returnValue;
  }

  // Helper to validate response and log warning if content type doesn't match
  // Note: We log warnings but don't throw errors, as the HTTP request itself succeeded
  private validateResponse(expectedMime: string) {
    return tap((response: HttpResponse<any>) => {
      const actualContentType = response.headers.get('Content-Type');
      this.logger.info("validateResponse","ApiService", `Validating response. Expected: ${expectedMime}, Actual: ${actualContentType || 'null'}, Status: ${response.status}, OK: ${response.ok}`);
      
      if (!this.validateContentType(expectedMime, actualContentType)) {
        this.logger.warn("validateResponse","ApiService", `Content-Type validation failed. Expected: ${expectedMime}, Actual: ${actualContentType}. Request succeeded but content type mismatch.`);
        // Don't throw error - just log warning, as the HTTP request itself was successful
      }
    });
  }

  // Variable holder for heartbeat
  heartbeat: Subscription;

  /**
   * Retrieve report data from Cognos
   * @param reportKey Key of report to request, see ReportTypes
   * @returns Observable<any[]>
   */
  getSavedReportData(reportKey: ReportTypes): Observable<any[]> {
    this.logger.info("getSavedReportData","ApiService",reportKey)
    // Enable loading
    this._store.dispatch( new ConfigActions.SetParameter('loading', true) );
    const reportInfo: ReportInfo = this._store.selectSnapshot(ConfigState.GetReportInfo)(reportKey);
    // const config = this._store.selectSnapshot<Config>(ConfigState);
    const config = this._store.snapshot().config;
    if (config.corpintra) {
      // Get report from Live Server
      // THIS IS COMPLICATED - BECAUSE 
      // FIRST GET ReportVersion
      // SECOND GET ReportVersionOutput
      // THIRD GET ReportVersionOutputContent

      let firstId = '';
      // FIRST !!!!
      this.logger.info("getSavedReportData","ApiService", `First !! Getting report info from: ${config.apiDomain}${config.apiLink}objects/${reportInfo.id}/versions`);
      return this.http.get<any>(`${config.apiDomain}${config.apiLink}objects/${reportInfo.id}/versions`, {
        params: this.expectResponse('application/json'),
        observe: 'response'
      }).pipe(
        this.validateResponse('application/json'),
        catchError(err => {
          // Catch error on getting info from Report ID
          this.logger.error("getSavedReportData","ApiService", `${reportKey} - Fail at getting report info. First!!!!`);
          this.logger.error("getSavedReportData","ApiService", `The error is: ${err}`);
          this.logger.error("getSavedReportData","ApiService", `The error is: ${err.message}`);
          this.logger.error("getSavedReportData","ApiService", `The error is: ${err.error}`);
          this.logger.error("getSavedReportData","ApiService", `The error is: ${err.status}`);
          this.logger.error("getSavedReportData","ApiService", `The error is: ${err.statusText}`);
          this.logger.error("getSavedReportData","ApiService", `The error is: ${err.headers}`);
          this.logger.error("getSavedReportData","ApiService", `The error is: ${err.ok}`);
          this.logger.error("getSavedReportData","ApiService", `The error is: ${err.status}`);
          return throwError(err);
        }),
        switchMap(json => {
          // Get report versions --- SECOND !!!
          firstId = json.body.data[0].id;
          const nextLink = json.body.data[0]._meta.links.outputs.url;
          this.logger.info("getSavedReportData","ApiService", `Second !! Getting report versions from: ${config.apiDomain}${nextLink}`);
          return this.http.get<any>(config.apiDomain + nextLink, {
            observe: 'response',
            params: this.expectResponse('application/json')
          }).pipe(
            this.validateResponse('application/json'),
            catchError(err => {
              // Catch error on getting report versions
              this.logger.error("getSavedReportData","ApiService", `${reportKey} - Fail at getting last report versions. Second!!!!`);
              this.logger.error("getSavedReportData","ApiService", `The error is: ${err}`);
              this.logger.error("getSavedReportData","ApiService", `The error is: ${err.message}`);
              this.logger.error("getSavedReportData","ApiService", `The error is: ${err.error}`);
              this.logger.error("getSavedReportData","ApiService", `The error is: ${err.status}`);
              this.logger.error("getSavedReportData","ApiService", `The error is: ${err.statusText}`);
              this.logger.error("getSavedReportData","ApiService", `The error is: ${err.headers}`);
              this.logger.error("getSavedReportData","ApiService", `The error is: ${err.ok}`);
              this.logger.error("getSavedReportData","ApiService", `The error is: ${err.status}`);
              return throwError(err);
            }),
            switchMap(json => {
              // Get last saved report content --- THIRD !!!!
              const csv = json.body.data.find(item => item.format === 'CSV');
              const html = json.body.data.find(item => item.format === 'HTML');

              // Check for Type and fetch the data different for CSV or HTML or unkown
              // 
              if (html && html._meta.links.content.mimeType) {
                // Report has mimeType of type HTML
                const nextLink = html._meta.links.content.url;
                this.reportDates[reportKey] = html.modificationTime;
                this.logger.info("getSavedReportData","ApiService", `Third !! Getting report table data in HTML format from: ${config.apiDomain}${nextLink}`);
                return this.http.get(config.apiDomain + nextLink, {
                  responseType: 'text',
                  observe: 'response',
                  params: this.expectResponse('/html'),
                }).pipe(
                  this.validateResponse('/html'),
                  catchError(err => {
                    // Catch error on getting HTML content
                    this.logger.error("getSavedReportData","ApiService", `${reportKey} - Fail at getting report table data in HTML format. Third!!!!`);
                    this.logger.error("getSavedReportData","ApiService", `Fail at getting report table data in HTML format: ${err}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.message}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.error}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.status}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.statusText}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.headers}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.ok}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.status}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${html}`);
                    return err;
                  }),
                  finalize(() => {
                    // Now we have the data and can deactivate the spinner
                    this._store.dispatch( 
                      new ConfigActions.SetParameter('loading', false) 
                    );
                  }),
                  map((response: HttpResponse<any>) => {
                    // Convert HTML / CSV to JSON and formatting
                    // MIME Type can be either text/html or application/csv
                    const mimeType = response.headers.get('Content-Type').split(';')[0].trim();
                    let columns = [];
                    // Test columns to be in numberFormat ... see config_common which columns are numebrs
                    try {
                      columns = config.reports[config.target].columns[reportKey].shouldBeNumber;
                    } catch (err) { }

                    // MIME Type can be either text/html or application/csv
                    let rows = [];
                    switch (mimeType) {
                      case 'text/html':
                        // Parse as HTML
                        rows = htmlToJson(response.body, `[lid=${reportInfo.selector}] tr`);
                        break;
                      case 'application/csv':
                        // Parse as CSV differently for deliveries report
                        if (reportKey === ReportTypes.Deliveries) {
                          rows = deliveriesToJson(response.body, this.language);
                        }
                        // Parse as CSV differently OrderBacklog & OIH with csvToJsonNamed
                        else if (reportKey === ReportTypes.OrderBacklog || reportKey === ReportTypes.OrderIntakeHistory) {
                          rows = csvToJsonNamed(response.body);
                        } else {
                          rows = csvToJson(response.body, columns);
                        }
                        break;
                      default:
                    }
                    // Convert fields to numbers
                    if (columns.length > 0) {
                      rows = rows.map(row => {
                        columns.forEach(num => {
                          row[num] = isNaN(row[num]) ? 0 : parseFloat(row[num]);
                        });
                        return row;
                      });
                    }
                    return rows;
                  })
                );
              } else if (csv && csv._meta.links.content.mimeType) {
                // Report has mimeType of type CSV
                const nextLink = csv._meta.links.content.url;
                this.reportDates[reportKey] = csv.modificationTime;
                return this.http.get(config.apiDomain + nextLink, {
                  responseType: 'text',
                  observe: 'response',
                  params: this.expectResponse('/csv')
                }).pipe(
                  this.validateResponse('/csv'),
                  catchError(err => {
                    // Catch error on getting CSV content
                    console.log(`${reportKey} - Fail at getting report table data in csv format`);
                    this.logger.error("getSavedReportData","ApiService", `Fail at getting report table data in csv format: ${err}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.message}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.error}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.status}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.statusText}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.headers}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.ok}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${err.status}`);
                    this.logger.error("getSavedReportData","ApiService", `The error is: ${csv}`);
                    return err;
                  }),
                  finalize(() => {
                    // Now we have the data and can deactivate the spinner
                    this._store.dispatch( 
                      new ConfigActions.SetParameter('loading', false) 
                    );
                  }),
                  map((response: HttpResponse<any>) => {
                    // Convert HTML / CSV to JSON and formatting
                    // MIME Type can be either text/html or application/csv
                    const mimeType = response.headers.get('Content-Type').split(';')[0].trim();
                    let columns = [];
                    // Test columns to be in numberFormat ... see config_common which columns are numebrs
                    try {
                      columns = config.reports[config.target].columns[reportKey].shouldBeNumber;
                    } catch (err) { }

                    // MIME Type can be either text/html or application/csv
                    let rows = [];
                    switch (mimeType) {
                      case 'text/html':
                        // Parse as HTML
                        rows = htmlToJson(response.body, `[lid=${reportInfo.selector}] tr`);
                        break;
                      case 'application/csv':
                        // Parse as CSV differently for deliveries report
                        if (reportKey === ReportTypes.Deliveries) {
                          rows = deliveriesToJson(response.body, this.language);
                        }
                        // Parse as CSV differently OrderBacklog & OIH with csvToJsonNamed
                        else if (reportKey === ReportTypes.OrderBacklog || reportKey === ReportTypes.OrderIntakeHistory) {
                          rows = csvToJsonNamed(response.body);
                        } else {
                          rows = csvToJson(response.body, columns);
                        }
                        break;
                      default:
                    }
                    // Convert fields to numbers
                    if (columns.length > 0) {
                      rows = rows.map(row => {
                        columns.forEach(num => {
                          row[num] = isNaN(row[num]) ? 0 : parseFloat(row[num]);
                        });
                        return row;
                      });
                    }
                    return rows;
                  })
                );
              } else {
                // Report doesn't have mimeType, it means we have to use second way of get it
                return this.http.get<any>(`${config.apiDomain}${config.apiLink}objects/${firstId}/items`, {
                  observe: 'response',
                  params: this.expectResponse('application/json')
                }).pipe(
                  this.validateResponse('application/json'),
                  catchError(err => {
                    // Catch error on getting first items
                    console.error(`${reportKey} - Fail at getting first items`);
                    return throwError(err);
                  }),
                  switchMap(data => {
                    // Filter items by "output"
                    const items: any[] = data.body.data.filter(item => item.type === 'output');
                    if (items.length === 0) {
                      return throwError('No output found');
                    }
                    // Sort items by modificationTime
                    items.sort((a, b) => moment(b.modificationTime, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]').valueOf() - moment(a.modificationTime, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]').valueOf());
                    const item = items[0];
                    // Set report date
                    this.reportDates[reportKey] = item.modificationTime;
                    // Return second request
                    return this.http.get<any>(`${config.apiDomain}${config.apiLink}objects/${item.id}/items`, {
                      observe: 'response',
                      params: this.expectResponse('application/json')
                    }).pipe(
                      this.validateResponse('application/json'),
                      catchError(err => {
                        // Catch error on getting second items
                        console.log(`${reportKey} - Fail at getting second items`);
                        return throwError(err);
                      }),
                      switchMap(data => this.http.get(`${config.apiDomain}${config.apiLink}disp/repository/sid/cm/oid/${data.body.data[0].id}/content`, {
                        observe: 'response',
                        responseType: 'text'
                      }))
                    );
                  }),
                  catchError(err => {
                    // Catch error on getting first items
                    console.log(`${reportKey} - Fail at getting first items`);
                    return throwError(err);
                  })
                );
              }
            }),
            catchError(err => {
              // Catch error on getting HTML content
              console.log(`${reportKey} - Fail at getting report table data`);
              this.logger.error("getSavedReportData","ApiService", `Error getting report table data: ${err}`);
              return throwError(err);  // Change from 'return err;' to 'return throwError(err);'
            }),
            finalize(() => {
              // Now we have the data and can deactivate the spinner
              this._store.dispatch( 
                new ConfigActions.SetParameter('loading', false) 
              );
            }),
            map((response: HttpResponse<any> | any[]) => {
              // If it's already an array (from HTML/CSV paths), just return it
              if (Array.isArray(response)) {
                // Only log part of the response, the report data might contains thousands of rows and columns, so we don't want to log the whole response
                this.logger.info("getSavedReportData","ApiService", `Response is already an array: ${response.slice(0, 10).join(', ')}...`);
                return response;
              }
              
              // Otherwise, it's a HttpResponse (from items path), process it
              if (!response || !response.headers) {
                this.logger.error("getSavedReportData","ApiService", "Invalid response object received");
                throw new Error("Invalid response object");
              }
              
              // Convert HTML / CSV to JSON and formatting
              // MIME Type can be either text/html or application/csv
              const contentType = response.headers.get('Content-Type');
              if (!contentType) {
                this.logger.warn("getSavedReportData","ApiService", "No Content-Type header found");
              }
              const mimeType = contentType ? contentType.split(';')[0].trim() : '';
              let columns = [];
              // Test columns to be in numberFormat ... see config_common which columns are numebrs
              try {
                columns = config.reports[config.target].columns[reportKey].shouldBeNumber;
              } catch (err) { }

              // MIME Type can be either text/html or application/csv
              let rows = [];
              switch (mimeType) {
                case 'text/html':
                  // Parse as HTML
                  rows = htmlToJson(response.body, `[lid=${reportInfo.selector}] tr`);
                  break;
                case 'application/csv':
                  // Parse as CSV differently for deliveries report
                  if (reportKey === ReportTypes.Deliveries) {
                    rows = deliveriesToJson(response.body, this.language);
                  }
                  // Parse as CSV differently OrderBacklog & OIH with csvToJsonNamed
                  else if (reportKey === ReportTypes.OrderBacklog || reportKey === ReportTypes.OrderIntakeHistory) {
                    rows = csvToJsonNamed(response.body);
                  } else {
                    rows = csvToJson(response.body, columns);
                  }
                  break;
                default:
              }
              // Convert fields to numbers
              if (columns.length > 0) {
                rows = rows.map(row => {
                  columns.forEach(num => {
                    row[num] = isNaN(row[num]) ? 0 : parseFloat(row[num]);
                  });
                  return row;
                });
              }
              return rows;
            })
          );
        }),
        catchError(err => {
          // Catch error on getting HTML content
          console.log(`${reportKey} - Fail at getting report table data`);
          return err;
        }),
        finalize(() => {
          // Now we have the data and can deactivate the spinner
          this._store.dispatch( 
            new ConfigActions.SetParameter('loading', false) 
          );
        }),
        map((response: HttpResponse<any> | any[]) => {
          // If it's already an array (from HTML/CSV paths), just return it
          if (Array.isArray(response)) {
            return response;
          }
          
          // Cast to HttpResponse to fix TypeScript errors
          const httpResponse = response as HttpResponse<any>;

          // Validate content type before processing
          const actualContentType = httpResponse.headers.get('Content-Type');
          // Determine expected type based on what was requested
          const expectedType = actualContentType?.includes('html') ? '/html' : 
                             actualContentType?.includes('csv') ? '/csv' : 
                             'application/json';
          
          if (!this.validateContentType(expectedType, actualContentType)) {
            this.logger.warn("getSavedReportData","ApiService", `Content type mismatch: Expected ${expectedType}, got ${actualContentType}`);
          }
          
          // Convert HTML / CSV to JSON and formatting
          // MIME Type can be either text/html or application/csv
          const mimeType = httpResponse.headers.get('Content-Type').split(';')[0].trim();
          let columns = [];
          // Test columns to be in numberFormat ... see config_common which columns are numebrs
          try {
            columns = config.reports[config.target].columns[reportKey].shouldBeNumber;
          } catch (err) { }

          // MIME Type can be either text/html or application/csv
          let rows = [];
          switch (mimeType) {
            case 'text/html':
              // Parse as HTML
              rows = htmlToJson(httpResponse.body, `[lid=${reportInfo.selector}] tr`);
              break;
            case 'application/csv':
              // Parse as CSV differently for deliveries report
              if (reportKey === ReportTypes.Deliveries) {
                rows = deliveriesToJson(httpResponse.body, this.language);
              }
              // Parse as CSV differently OrderBacklog & OIH with csvToJsonNamed
              else if (reportKey === ReportTypes.OrderBacklog || reportKey === ReportTypes.OrderIntakeHistory) {
                rows = csvToJsonNamed(httpResponse.body);
              } else {
                rows = csvToJson(httpResponse.body, columns);
              }
              break;
            default:
          }
          // Convert fields to numbers
          if (columns.length > 0) {
            rows = rows.map(row => {
              columns.forEach(num => {
                row[num] = isNaN(row[num]) ? 0 : parseFloat(row[num]);
              });
              return row;
            });
          }
          return rows;
        })
      );
    } else {
      this.logger.info("getSavedReportData","ApiService", "Get report from local")
      // Get report from local
      // Check if the file is a text file (like Deliveries.txt) that needs to be parsed as text
      const isTextFile = reportKey === ReportTypes.Deliveries || reportInfo.fallback.endsWith('.txt');

      if (isTextFile) {
        // Handle text files (TSV, CSV, etc.)
        return this.http.get(`assets/reports/${reportInfo.fallback}`, {
          observe: 'response',
          responseType: 'text',
        }).pipe(
          // Disable loading on finish
          finalize(() => {
            this._store.dispatch( new ConfigActions.SetParameter('loading', false) );
          }),
          // Set current date
          tap(_ => {
            this.reportDates[reportKey] = moment().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
          } ),
          map((res: HttpResponse<string>) => {
            return res.body;
          }),
          map((json: string) => {
            if (reportKey === ReportTypes.Deliveries) {
              return deliveriesToJson(json, this.language);
            }
            return json as any;
          })
        ) as Observable<any[]>;
      } else {
        // Handle JSON files
        return this.http.get(`assets/reports/${reportInfo.fallback}`, {
          observe: 'response',
          responseType: 'text',  // Add this to prevent automatic JSON parsing
          params: this.expectResponse('application/json'),
        }).pipe(
          this.validateResponse('application/json'),
          // Disable loading on finish
          finalize(() => {
            this._store.dispatch( new ConfigActions.SetParameter('loading', false) );
          }),
          // Set current date
          tap(_ => {
            this.reportDates[reportKey] = moment().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
          } ),
          map((res: HttpResponse<string>) => {  // Type the response as HttpResponse<string>
            let result = null;
            try {
              result = JSON.parse(res.body)
            } catch (error) {
              this.logger.error("getSavedReportData","ApiService", `Failed to parse JSON: ${error}`);
              this.logger.info("getSavedReportData","ApiService", `If the parsing fails, this means that the file is not a JSON file, but a text file (like Deliveries.txt) that needs to be parsed as text`);
              this.logger.info("getSavedReportData","ApiService", `The file is: ${reportInfo.fallback}`);
              this.logger.info("getSavedReportData","ApiService", `You need to check, if the file really is a JSON file and not a text file (like Deliveries.txt)`);
              result = res.body;
            }
            return result;
          }),
          map(json => {
            return json;
          })
        );
      }
    }

  }
}
